import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'

import Meta from '../../components/Meta'

import { getBaseTitle, toLocaleUrl, getTranslateValue } from '../../utils/locale'
import { getSizedImageUrl } from '../../utils/misc'
import { SEO_IMAGE_META, getSEOValue } from '../../utils/metaUtils'

import { ProgramDetailFragment } from '../../types/generated'

const buildKeywords = (program: ProgramDetailFragment, language: string) => {
  const categoriesAndGenres = program.categories.concat(program.genres)

  return categoriesAndGenres.map(
    category => getTranslateValue(category, 'seo_title', language) ?? getTranslateValue(category, 'title', language)
  )
}

interface Props {
  program: ProgramDetailFragment
}

const ProgramPageMeta: FC<Props> = ({ program }) => {
  const {
    i18n: { language },
  } = useTranslation()
  const seoImageUrl = getSizedImageUrl(
    getTranslateValue(program.images, 'poster'),
    SEO_IMAGE_META.width,
    SEO_IMAGE_META.height
  )

  const title = getSEOValue({
    target: program,
    key: 'title',
    language,
    fallbackValue: `${getTranslateValue(program, 'title', language)}-${getBaseTitle(language)}`,
  })

  const description = getSEOValue({
    target: program,
    key: 'synopsis',
    language,
    fallbackValue: getTranslateValue(program, 'synopsis', language),
  })

  return (
    <Meta
      title={title!}
      description={description!}
      keywords={getSEOValue({ target: program, key: 'keyword', language }) ?? buildKeywords(program, language)}
      imageUrl={seoImageUrl}
      uri={toLocaleUrl(`/program/${program.slug}`)}
      schemaJSON={{
        '@type': program.meta?.isSeries ? 'TVSeries' : 'Movie',
        name: getSEOValue({
          target: program,
          key: 'title',
          language,
          fallbackValue: getTranslateValue(program, 'title', language),
        }),
        image: {
          '@type': 'ImageObject',
          url: seoImageUrl,
          width: SEO_IMAGE_META.width,
          height: SEO_IMAGE_META.height,
        },
        genre: program.genres.map(category => getTranslateValue(category, 'title', language)),
        countryOfOrigin: program.meta?.produceCountries,
        contentLocation: program.meta?.produceCountries,
        numberOfEpisodes: program.meta?.episodeTotal,
        director: program.people
          .filter(people => people.role === 'director')
          .map(people => ({
            '@type': 'PerformanceRole',
            director: {
              '@type': 'Person',
              name: getTranslateValue(people, 'name', language),
            },
          })),
        actor: program.people
          .filter(people => people.role === 'cast')
          .map(people => ({
            '@type': 'PerformanceRole',
            actor: {
              '@type': 'Person',
              name: getTranslateValue(people, 'name', language),
            },
          })),
        publisher: {
          '@type': 'Organization',
          name: program.contentProvider?.code,
        },
      }}
    />
  )
}

export default ProgramPageMeta
